<template>
  <div>
    <b-row class="mb-3">
      <b-col md="1.5" class="ml-3">
        <b-select
          v-if="pagination"
          v-model="pagination.perPage"
          :options="pageOptions"
          class="float-left"
          @change="paginationChanged"
        >
        </b-select>
      </b-col>
    </b-row>
    <b-table
      v-if="pagination"
      id="trainings-table"
      hover
      borderless
      :items="trainings"
      :fields="fields"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :per-page="0"
      :current-page="pagination.currentPage"
      @sort-changed="sortingChanged"
      :no-local-sorting="true"
      responsive
      show-empty
      thead-class="esep-blue-bg text-light"
      :tbody-tr-attr="rowClass"
    >
      <template v-slot:cell(action)="row">
        <router-link :to="{ path: `/announcement/${row.item.id}` }">
          <b-button
            size="sm"
            variant="transparent"
            v-b-tooltip.hover
            :title="$t('trainings.showAnnouncement')"
          >
            <b-icon icon="eye"></b-icon>
          </b-button>
        </router-link>
        <router-link :to="{ path: `/training/${row.item.id}` }" v-if="actions.view">
          <b-button
            size="sm"
            variant="transparent"
            v-b-tooltip.hover
            :title="$t('trainings.showTraining')"
          >
            <b-icon icon="folder-symlink"></b-icon>
          </b-button>
        </router-link>
        <template v-if="actions.apply">
          <b-button
            size="sm"
            variant="transparent"
            v-b-tooltip.hover
            :title="$t('trainings.applyTraining')"
            @click="showApplyToTraining(row.item)"
          >
            <b-icon icon="calendar2-check"></b-icon>
          </b-button>
        </template>
      </template>

      <template v-slot:cell(icon)="row">
        <b-img
          :src="row.item.module.iconPath"
          :alt="row.item.module.iconPath"
          class="img-fluid w-25 p-2"
        />
      </template>

      <template v-slot:empty="">
        <h6>{{ $t("trainings.emptyTable") }}</h6>
      </template>
    </b-table>

    <b-row>
      <b-col>
        <b-pagination
          v-if="pagination"
          v-model="pagination.currentPage"
          @input="paginationChanged"
          :total-rows="trainingsCount"
          :per-page="pagination.perPage"
          align="right"
          aria-controls="trainings-table"
          variant="info"
        ></b-pagination>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { prettifyDate } from "../../services/utils";

export default {
  mounted() {
    if (this.$store.state.pagination) {
      this.pagination = this.$store.state.pagination;
    } else {
      this.pagination = this.startingPagination;
    }
    if (this.$store.state.sortDesc) {
      this.sortDesc = this.$store.state.sortDesc;
    } else {
      this.sortDesc = null;
    }
    if (this.$store.state.sortBy) {
      this.sortBy = this.$store.state.sortBy;
    } else {
      this.sortBy = "id";
    }
  },
  props: {
    trainings: Array,
    trainingsCount: Number,
    actions: Object,
  },
  data() {
    return {
      sortBy: null,
      sortDesc: null,
      pagination: null,
      startingPagination: {
        perPage: 10,
        currentPage: 1,
      },
      pageOptions: [10, 20, 50],
      fields: [
        { key: "id", label: "Κωδικός Εκπαίδευσης", sortable: true },
        { key: "activityNo", label: "Αριθμός", sortable: true }, // needs to be translated
        { key: "icon", label: "", sortable: false }, // needs to be translated
        {
          key: "module",
          label: "Περιγραφή",
          sortable: true,
          filterByFormatted: true,
          formatter: "showTitle",
          thClass: "text-left",
          tdClass: "text-left",
        },
        {
          key: "leader",
          label: "Αρχηγός",
          sortable: true,
          filterByFormatted: true,
          formatter: "showLeader",
          thClass: "text-left",
          tdClass: "text-left",
        },
        {
          key: "applicationsExpireDate",
          label: "Ημ/νια Λήξης Αιτήσεων",
          sortable: true,
          formatter: "prettifyDate",
        },
        {
          key: "periods",
          label: "Ημ/νιες Διεξαγωγής",
          sortable: true,
          filterByFormatted: true,
          formatter: "showPeriods",
          tdClass: "maxWidth",
        },
        {
          key: "organizerUnit.name",
          label: "Υπέυθυνο Κλιμάκιο",
          sortable: true,
          thClass: "text-left",
          tdClass: "text-left",
        },
        { key: "action", label: "Ενέργειες", sortable: false },
      ],
    };
  },
  methods: {
    prettifyDate(date) {
      return prettifyDate(date);
    },
    showTitle(mod) {
      return mod.title;
    },
    showLeader(leader) {
      if (leader && leader.member) {
        return leader.member.surName + " " + leader.member.firstName;
      } else {
        return "";
      }
    },
    showPeriods(periods) {
      let periodText = "";
      if (periods.length > 0) {
        for (let i = 0; i < periods.length; i++) {
          const period = periods[i];
          let startDate = new Date(period.startDate);
          let endDate = new Date(period.endDate);
          periodText += `${startDate.toLocaleString()}   - ${endDate.toLocaleString()} (${
            period.location
          })`;
          if (periods.length > 1 && i < periods.length - 1) {
            periodText += ` , `;
          }
        }
      } else {
        periodText = "-";
      }
      return periodText;
    },
    rowClass(item) {
      return { style: `background-color: ${item?.module.color};` };
    },
    paginationChanged() {
      this.$store.state.pagination = this.pagination;
      this.$emit("pagination-changed", this.pagination);
    },
    showApplyToTraining(training) {
      this.$emit("show-apply-training", training);
    },
    sortingChanged(ctx) {
      let sort_string;
      switch (ctx.sortBy) {
        case "id":
          sort_string = "ac.id";
          break;
        case "activityNo":
          sort_string = "ac.activityNo";
          break;
        case "leaders":
          sort_string = "l.member.surName";
          break;
        case "leader":
          sort_string = "l.member.surName";
          break;
        case "module":
          sort_string = "m.title";
          break;
        case "periods":
          sort_string = "p.startDate";
          break;
        case "organizerUnit.name":
          sort_string = "ac.organizerUnit.name";
          break;
        case "applicationsExpireDate":
          sort_string = "ac.applicationsExpireDate";
          break;
        default:
          sort_string = "ac.applicationsExpireDate";
      }

      let sorting = `${sort_string} ${!ctx.sortDesc ? "" : " desc"}`;

      this.$store.state.sortBy = ctx.sortBy;
      this.$store.state.sortDesc = ctx.sortDesc;
      this.$emit("sorting-changed", sorting);
    },
  },
};
</script>

<style>
.maxWidth {
  max-width: 400px;
}
</style>
